import dayjs from "./dayjs-config.util";

/**
 * Parses a tracking offset string like '1h', '12h' into a number of hours.
 * Returns `null` for non-numeric values (e.g. `'now'`) or on parse failure.
 */
export function parseHourOffset(value: string): number | null {
  const hours = parseInt(value.replace('h', ''), 10);
  return isNaN(hours) ? null : hours;
}

export function getFormattedDates(start: string, end: string, timezone?: string): string | null {
  if (!start && !end) {
    return null;
  }

  const startDate = parseDate(start, timezone);

  if (start && !end) {
    return startDate.format("ddd, MM/DD HH:mm z");
  }

  const endDate = parseDate(end, timezone);
  const isSameDay = startDate.isSame(endDate, "day");

  if (isSameDay) {
    const formattedStart = startDate.format("ddd, MM/DD HH:mm");
    const formattedEnd = endDate.format("HH:mm z");
    return formattedStart === formattedEnd ? startDate.format("ddd, MM/DD HH:mm z") : `${formattedStart} – ${formattedEnd}`;
  }

  const formattedStart = startDate.format("ddd, MM/DD HH:mm z");
  const formattedEnd = endDate.format("ddd, MM/DD HH:mm z");
  return formattedStart === formattedEnd ? formattedStart : `${formattedStart} - ${formattedEnd}`;
}

export function getFormattedDate(date: string, timezone?: string, omitTimezone: boolean = false): string {
  if (!date) {
    return "";
  }

  let currentDate;

  try {
    currentDate = dayjs.utc(date).tz(resolveTimezone(date, timezone));
  } catch (error) {
    if (error instanceof RangeError) {
      return "";
    }

    throw error;
  }

  let dateFormat = currentDate.year() === dayjs.utc().year() ? "MMM D, HH:mm" : "MMM D, YYYY HH:mm";

  if (!omitTimezone) {
    dateFormat += " z";
  }

  return currentDate.format(dateFormat);
}

export function parseDate(dateStr: string, timezone?: string): dayjs.Dayjs {
  try {
    return dayjs.utc(dateStr).tz(resolveTimezone(dateStr, timezone));
  } catch (error) {
    throw error;
  }
}

export function resolveTimezone(dateStr: string, timezone?: string): string {
  if (timezone) {
    return timezone;
  }

  if (dateStr.endsWith('Z')) {
    return 'UTC';
  }

  const offsetMatch = dateStr.match(/([+-])(\d{2}):(\d{2})$/);
  if (!offsetMatch) {
    return dayjs.tz.guess();
  }

  const [, sign, hours, minutes] = offsetMatch;
  const targetOffset = (sign === '-' ? -1 : 1) * (parseInt(hours) * 60 + parseInt(minutes));

  const utcDate = dayjs.utc(dateStr);
  const candidates = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
  ];

  for (const tz of candidates) {
    if (utcDate.tz(tz).utcOffset() === targetOffset) {
      return tz;
    }
  }

  return dayjs.tz.guess();
}

const TIMEZONE_MAP: { iana: string; abbr: Record<number, string> }[] = [
  { iana: 'Pacific/Honolulu', abbr: { [-600]: 'HST' } },
  { iana: 'America/Anchorage', abbr: { [-540]: 'AKST', [-480]: 'AKDT' } },
  { iana: 'America/Los_Angeles', abbr: { [-480]: 'PST', [-420]: 'PDT' } },
  { iana: 'America/Denver', abbr: { [-420]: 'MST', [-360]: 'MDT' } },
  { iana: 'America/Phoenix', abbr: { [-420]: 'MST' } },
  { iana: 'America/Chicago', abbr: { [-360]: 'CST', [-300]: 'CDT' } },
  { iana: 'America/New_York', abbr: { [-300]: 'EST', [-240]: 'EDT' } },
  { iana: 'America/Halifax', abbr: { [-240]: 'AST', [-180]: 'ADT' } },
  { iana: 'America/St_Johns', abbr: { [-210]: 'NST', [-150]: 'NDT' } },
  { iana: 'America/Sao_Paulo', abbr: { [-180]: 'BRT' } },
  { iana: 'Atlantic/Reykjavik', abbr: { [0]: 'GMT' } },
  { iana: 'Europe/London', abbr: { [0]: 'GMT', [60]: 'BST' } },
  { iana: 'Europe/Paris', abbr: { [60]: 'CET', [120]: 'CEST' } },
  { iana: 'Europe/Athens', abbr: { [120]: 'EET', [180]: 'EEST' } },
  { iana: 'Europe/Moscow', abbr: { [180]: 'MSK' } },
  { iana: 'Asia/Dubai', abbr: { [240]: 'GST' } },
  { iana: 'Asia/Karachi', abbr: { [300]: 'PKT' } },
  { iana: 'Asia/Kolkata', abbr: { [330]: 'IST' } },
  { iana: 'Asia/Kathmandu', abbr: { [345]: 'NPT' } },
  { iana: 'Asia/Dhaka', abbr: { [360]: 'BDT' } },
  { iana: 'Asia/Bangkok', abbr: { [420]: 'ICT' } },
  { iana: 'Asia/Shanghai', abbr: { [480]: 'CST' } },
  { iana: 'Asia/Tokyo', abbr: { [540]: 'JST' } },
  { iana: 'Australia/Adelaide', abbr: { [570]: 'ACST', [630]: 'ACDT' } },
  { iana: 'Australia/Sydney', abbr: { [600]: 'AEST', [660]: 'AEDT' } },
  { iana: 'Pacific/Auckland', abbr: { [720]: 'NZST', [780]: 'NZDT' } },
];

export function getFormattedDateWithOriginalOffset(date: string): string {
  if (!date) return '';

  const offsetMinutes = extractOffsetMinutes(date);
  const utcDate = dayjs.utc(date);
  const d = utcDate.utcOffset(offsetMinutes);

  const dateFormat = d.year() === dayjs.utc().year() ? 'MMM D, HH:mm' : 'MMM D, YYYY HH:mm';
  const abbr = resolveAbbreviation(utcDate, offsetMinutes);

  return `${d.format(dateFormat)} ${abbr}`;
}

function extractOffsetMinutes(dateStr: string): number {
  if (dateStr.endsWith('Z')) return 0;

  const match = dateStr.match(/([+-])(\d{2}):(\d{2})$/);
  if (!match) return 0;

  const [, sign, hours, minutes] = match;
  return (sign === '-' ? -1 : 1) * (parseInt(hours) * 60 + parseInt(minutes));
}

function resolveAbbreviation(utcDate: dayjs.Dayjs, targetOffset: number): string {
  for (const { iana, abbr } of TIMEZONE_MAP) {
    if (utcDate.tz(iana).utcOffset() === targetOffset && abbr[targetOffset]) {
      return abbr[targetOffset];
    }
  }

  if (targetOffset === 0) return 'UTC';

  const sign = targetOffset >= 0 ? '+' : '-';
  const totalMin = Math.abs(targetOffset);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;

  return m > 0 ? `UTC${sign}${h}:${String(m).padStart(2, '0')}` : `UTC${sign}${h}`;
}

export function formatForApi(date: Date | null): string | null{
  return date
    ? dayjs(date).format('YYYY-MM-DDTHH:mm:ssZ')
    : null;
}

